import { useState, useCallback, useRef, useEffect } from 'react';
import { getMessages } from '../MessagesServices';

const useMessageFetching = (selectedConversation, handleError) => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const messageRefs = useRef({});
    const perPage = 10;

    const fetchMessages = useCallback(async (page = 1, shouldAppend = false) => {
        if (!selectedConversation) return;

        setLoading(true);
        try {
            const { data, metadata } = await getMessages({
                conversation_id: selectedConversation.id,
                page,
                size: perPage,
            });

            setHasMore(metadata.has_next);
            setMessages(prev => shouldAppend ? [...prev, ...data] : data);
            setCurrentPage(page);
        } catch (error) {
            handleError(error);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    }, [selectedConversation, handleError]);

    const handleScroll = useCallback((event) => {
        const container = event.currentTarget;
        const { scrollTop, scrollHeight, clientHeight } = container;
        const threshold = 50;

        const maxScrollTop = -(scrollHeight - clientHeight);
        const scrolledToTop = scrollTop <= maxScrollTop + threshold;

        if (scrolledToTop && !loading && hasMore) {
            const prevScrollTop = scrollTop;

            fetchMessages(currentPage + 1, true).then(() => {
                requestAnimationFrame(() => {
                    container.scrollTop = prevScrollTop;
                });
            });
        }
    }, [loading, hasMore, currentPage, fetchMessages]);

    useEffect(() => {
        if (selectedConversation) {
            setMessages([]);
            setCurrentPage(1);
            setHasMore(true);
            fetchMessages(1, false);
        }
    }, [selectedConversation, fetchMessages]);

    return {
        messages,
        loading,
        hasMore,
        messageRefs,
        handleScroll,
        setMessages
    };
};

export default useMessageFetching;